import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { isEqual, uniqWith } from 'lodash-es';
import moment from 'moment';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useEffect, useMemo, useState } from 'react';
import ControlBarActivity, {
  CONTROL_BAR_ACTIVITY_MIN_HEIGHT,
  POST_ACTIVITIES_END_OF_TIME,
} from './ControlBarActivity';
import * as styles from './ControlBarActivityButton.module.scss';
import ControlBarButton from './ControlBarButton';
import ControlBarPopOver from './ControlBarPopOver';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import { useQuill } from '../../editor/QuillContext';
import isInlineTextInsertion from '../../editor/utils/isInlineTextInsertion';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated/gql';
import { PostActivityAction } from '../../graphql/generated/graphql';
import graphStatsIcon from '../../icons/non-prefilled/16x16/graphStats.svg';
import { UIActions } from '../../ui/uiSlice';
import { isFeatureEnabled } from '../../utils/features';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import collectThreadAndAnnotationIds from '../utils/collectThreadAndAnnotationIds';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';

const POST_FRAGMENT = gql(`
  fragment ControlBarActivityButton_post on Post {
    id
    insertedAt
    publishedAt
    userViewedAt
    threads {
      id
    }
    ...usePostPolicyCheck_post
  }
`);

const POST_ACTIVITIES_FRAGMENT = gql(`
  fragment ControlBarActivityButton_activities on PostActivity {
    actedAt
    action
    actors {
      id
    }
  }
`);

const ACTIVITIES_POPOVER_QUERY = gql(/** @complexity 37 */ `
  query ControlBarActivityButton($postId: ID!, $beforeAt: Datetime!, $withPostData: Boolean!) {
    postActivities(postId: $postId, beforeAt: $beforeAt) {
      ...ControlBarActivity_activities
    }
    post(id: $postId) @include(if: $withPostData) {
      id
      ...ControlBarActivity_post
    }
  }
`);

interface ControlBarActivityButtonProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  post: DocumentType<typeof POST_FRAGMENT>;
  activities: DocumentType<typeof POST_ACTIVITIES_FRAGMENT>[];
}

const ControlBarActivityButton = ({
  open,
  setOpen,
  post,
  activities,
}: ControlBarActivityButtonProps) => {
  const dispatch = useAppDispatch();
  const [isPrefetchingPopOverData, setIsPrefetchingPopOverData] =
    useState(false);
  const [isPreviousOpen, setIsPreviousOpen] = useState(open);
  if (isPreviousOpen !== open) {
    setIsPreviousOpen(open);
    setIsPrefetchingPopOverData(false);
  }

  const skipPopoverQuery = !open && !isPrefetchingPopOverData;
  const popoverQuery = useQuery(ACTIVITIES_POPOVER_QUERY, {
    variables: {
      // useQuery doesn't update `loading` in the result after
      // toggling `skip` unless we change the query variables
      postId: skipPopoverQuery ? '' : post.id,
      beforeAt: POST_ACTIVITIES_END_OF_TIME,
      withPostData: true,
    },
    skip: skipPopoverQuery,
  });

  useEffect(() => {
    if (open && popoverQuery.error && !popoverQuery.loading) {
      setOpen(false);
      dispatch(
        UIActions.pushToast({
          level: 'warning',
          message:
            'Getting post activities failed. Please check your network connection and try again.',
        }),
      );
    }
  }, [dispatch, popoverQuery.loading, popoverQuery.error, open, setOpen]);

  const fetchMoreActivities = async () => {
    const lastActivity = popoverQuery.data?.postActivities?.at(-1);
    if (!lastActivity) {
      return;
    }

    // There might be two activities with the same timestamp on the edge of two pages.
    // To ensure we don't skip any of them, we slightly over-fetch to include
    // the last activity from the previous page on the next page.
    const beforeAt = moment(lastActivity.actedAt).add(1, 'ms').toISOString();

    await popoverQuery.fetchMore({
      variables: {
        postId: post.id,
        beforeAt,
        withPostData: false,
      },
      updateQuery(previousResult, { fetchMoreResult }) {
        return {
          ...previousResult,
          postActivities: uniqWith(
            [
              ...previousResult.postActivities,
              ...fetchMoreResult.postActivities,
            ],
            isEqual,
          ),
        };
      },
    });
  };

  const postRenderTime = useMemo(
    () => ({ postId: post.id, time: moment() }), // Reset when active post changes
    [post.id],
  ).time;

  const unresolvedCommentThreadIds = useUnresolvedCommentThreadIds(post);

  const currentUser = useAppSelector((state) => state.session.user);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const postPolicyCheck = usePostPolicyCheck();
  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);

  const [lastUserViewBeforeOpen, setLastUserViewBeforeOpen] = useState<
    number | string
  >(post.userViewedAt ?? 0);

  if (
    lastUserViewBeforeOpen !== post.userViewedAt &&
    postRenderTime.isAfter(post.userViewedAt)
  ) {
    setLastUserViewBeforeOpen(post.userViewedAt);
  }

  const lastUserActivityBeforeOpen =
    activities.find(
      (activity) =>
        activity.actors.some((actor) => actor.id === currentUser.id) &&
        postRenderTime.isAfter(activity.actedAt),
    )?.actedAt ?? 0;

  const lastViewedAt = Math.max(
    new Date(lastUserViewBeforeOpen).getTime(),
    new Date(lastUserActivityBeforeOpen).getTime(),
  );

  const hasNewActivities =
    lastViewedAt !== 0 &&
    activities.some((activity) => {
      const isEmptyEdit =
        activity.actors.length === 0 &&
        activity.action === PostActivityAction.Edit;

      return (
        moment(activity.actedAt).isAfter(lastViewedAt) &&
        !isEmptyEdit &&
        !activity.actors.some((actor) => actor.id === currentUser.id)
      );
    });

  const isNewDraft =
    !post.publishedAt &&
    !activities.some((item) => item.action === PostActivityAction.Publish) &&
    moment(post.insertedAt).add(1, 'hour').isAfter(postRenderTime);

  return (
    isFeatureEnabled('post_activity') &&
    !isNewDraft &&
    !isMobile && (
      <ControlBarPopOver
        content={
          <ControlBarActivity
            post={popoverQuery.error ? undefined : popoverQuery.data?.post}
            activities={
              popoverQuery.error ? undefined : popoverQuery.data?.postActivities
            }
            unresolvedCommentThreadIds={unresolvedCommentThreadIds}
            fetchMoreActivities={fetchMoreActivities}
            closePopover={() => setOpen(false)}
            lastViewedAt={lastViewedAt}
            allowUnresolvedThreadsSection={canEdit}
          />
        }
        open={open}
        onOpenChange={setOpen}
        placement="bottom-start"
        preventScreenOverflow={{
          minHeight: CONTROL_BAR_ACTIVITY_MIN_HEIGHT,
        }}
      >
        <ControlBarButton
          active={open}
          aria-label="Post activities"
          onPointerDown={() => {
            if (!open) {
              setIsPrefetchingPopOverData(true);
            }
          }}
          icon={
            <SVGIcon
              className={classNames({
                [styles.hasNewActivities]: hasNewActivities,
                [styles.hasUnresolvedCommentThreads]:
                  canEdit && unresolvedCommentThreadIds.length > 0,
              })}
            >
              {graphStatsIcon}
            </SVGIcon>
          }
        />
      </ControlBarPopOver>
    )
  );
};

const useUnresolvedCommentThreadIds = (
  post: DocumentType<typeof POST_FRAGMENT>,
) => {
  const { quill } = useQuill();
  const [postThreadsIds, setPostThreadsIds] = useState(new Set<string>());
  useEffect(() => {
    if (!quill) return undefined;

    const updatePostThreadsIds = () => {
      const result = collectThreadAndAnnotationIds(quill.getContents().ops);
      setPostThreadsIds((current) =>
        isEqual(current, result.allThreadIds) ? current : result.allThreadIds,
      );
    };

    updatePostThreadsIds();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      updatePostThreadsIds();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill]);

  return post.threads
    .map((thread) =>
      isCommentOrPublicCommentThread(thread) && postThreadsIds.has(thread.id)
        ? thread.id
        : null,
    )
    .filter((item) => item != null);
};

export default ControlBarActivityButton;
